// Round identity shown to users: a 1-based position within a gaming day
// (1..288 on the 5-minute grid, 1..24 hourly) plus that day's date.
//
// The gaming day is anchored to the ACCOUNT'S REGISTRATION COUNTRY timezone
// (Brazil = UTC-3), never to the device. Product decision 2026-09-10: every
// Brazilian player must read the same `#043` for the same draw — a player in
// Manaus, a player in São Paulo, and a player travelling abroad. Numbering off
// the device made `#087` name two different draws depending on who was looking,
// which breaks support tickets and player-to-player conversation alike.
//
// What does NOT follow this anchor: the clock times printed next to a round, the
// day headings a list is grouped under, and the "today/yesterday" labels. Those
// stay on the viewer's own device timezone, because a date is something players
// read and reconcile against their own day, while `#043` is an opaque handle.
// The visible consequence is that a non-UTC-3 viewer's day can contain one seq
// reset (`#002, #001, #288, #287…`); `BetHistorySheet` marks that seam.
//
// The stable cross-country id remains the global `round_number`, shown as
// "global #…" on the draw-detail screen.
//
// NOTE: pass a timestamp that lies INSIDE the round window — the round's
// starts_at, or a bet's placed_at. Do NOT pass ends_at/cutoff: that is the
// exclusive right boundary of the slot and floors into the NEXT round.

const SLOT_SEC = 300;

/**
 * The timezone the gaming day is numbered in.
 *
 * App-global on purpose: one signed-in account has exactly one registration
 * country, and a guest has exactly one inferred country, so there is never a
 * moment where two anchors are live at once. `countryStore` owns the value and
 * pushes it here whenever the active country changes — see `syncRoundAnchor`.
 *
 * Undefined means "not told yet", which falls back to the device zone. That is
 * the pre-2026-09 behaviour and the only sane answer before the country list has
 * loaded: it keeps first paint numbering something rather than nothing, and the
 * store corrects it within the same session.
 */
let anchorTimeZone: string | undefined;

export function setRoundAnchorTimeZone(timeZone: string | null | undefined) {
  const next = String(timeZone || '').trim();
  anchorTimeZone = next || undefined;
}

export function roundAnchorTimeZone(): string | undefined {
  return anchorTimeZone;
}


/**
 * Seconds per round for a betting program. Milhar Hora draws hourly; everything
 * else runs on the 5-minute grid.
 *
 * Slotting an hourly bet on the 5-minute grid is not a rounding difference — it
 * numbers it 1..288 while the game page numbers the same round 1..24, so the two
 * screens can never agree and the player cannot take the number on their ticket
 * and find that round in the results list.
 */
export function programSlotSeconds(program: string | null | undefined): number {
  return program === 'milhar-hora' ? 3600 : SLOT_SEC;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Wall-clock fields of `ts` as read in `timeZone`. No `timeZone` means the
 * device's own zone, which is what every caller wanted before the anchor existed
 * and what the day-grouping helpers still want.
 */
function zonedFields(ts: number, timeZone?: string) {
  const date = new Date(ts * 1000);
  if (!timeZone) {
    return {
      year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(),
      hour: date.getHours(), minute: date.getMinutes(), second: date.getSeconds(),
    };
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date);
  const field = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
  // Some ICU builds render midnight as hour 24 under hour12:false.
  const hour = field('hour') % 24;
  return {
    year: field('year'), month: field('month'), day: field('day'),
    hour, minute: field('minute'), second: field('second'),
  };
}

/**
 * The gaming day (`date`) and 1-based slot (`seq`) a timestamp falls in.
 *
 * Numbering is by WALL CLOCK inside `timeZone`, not by elapsed seconds since
 * midnight, so the seq and the clock always agree: 14:00 is always slot 169 on
 * the 5-minute grid. On a daylight-saving transition that means a day yields
 * fewer (spring) or repeated (autumn) slots. No live market observes DST today —
 * Brazil dropped it in 2019 and Argentina does not use it — but a market that
 * does will need a rule here before it launches.
 */
export function gamingRound(
  tsInWindowSec: number | null | undefined,
  slotSec: number = SLOT_SEC,
  timeZone?: string,
): { date: string; seq: number } | null {
  const ts = Number(tsInWindowSec || 0);
  if (!ts) return null;
  const slot = slotSec > 0 ? slotSec : SLOT_SEC;
  const at = zonedFields(ts, timeZone);
  const secondsIntoDay = at.hour * 3600 + at.minute * 60 + at.second;
  return {
    date: `${at.year}-${pad2(at.month)}-${pad2(at.day)}`,
    seq: Math.floor(secondsIntoDay / slot) + 1, // 1..288 at 5 min, 1..24 hourly
  };
}

// Zero-padded sequence only, e.g. "044". '' when the timestamp is missing.
// Hourly rounds pad to two digits, matching the game page's #01..#24.
export function gamingRoundSeq(
  tsInWindowSec: number | null | undefined,
  slotSec: number = SLOT_SEC,
  timeZone: string | undefined = anchorTimeZone,
): string {
  const round = gamingRound(tsInWindowSec, slotSec, timeZone);
  if (!round) return '';
  return String(round.seq).padStart(slotSec >= 3600 ? 2 : 3, '0');
}

// Full identity label, e.g. "2026-07-12 · #044". '' when the timestamp is missing.
export function gamingRoundLabel(
  tsInWindowSec: number | null | undefined,
  timeZone: string | undefined = anchorTimeZone,
): string {
  const round = gamingRound(tsInWindowSec, SLOT_SEC, timeZone);
  return round ? `${round.date} · #${String(round.seq).padStart(3, '0')}` : '';
}

// Convenience for a round-like object (Archive / room current round). Prefers
// starts_at, then cutoff_ts — the API sets cutoff_ts to the window START (bets
// close the moment the draw window opens: round 30934 reports starts_at and
// cutoff_ts both at 09:50Z, ends_at at 09:55Z). Only ends_at is the exclusive
// right boundary, so only ends_at gets a slot subtracted; feeding it straight
// into the timestamp helpers above would floor into the NEXT round.
//
// Treating cutoff_ts as a right boundary (subtracting a slot from it) is what
// made the settlement summary title read one round behind the bet sheet and the
// bet history for the very same round.
type RoundLike = { starts_at?: number; ends_at?: number; cutoff_ts?: number } | null | undefined;

function windowStartTs(round: RoundLike): number {
  const start = Number(round?.starts_at || 0) || Number(round?.cutoff_ts || 0);
  if (start) return start;
  const end = Number(round?.ends_at || 0);
  return end ? end - SLOT_SEC : 0;
}

export function roundSeqFromRound(
  round: RoundLike,
  slotSec: number = SLOT_SEC,
  timeZone: string | undefined = anchorTimeZone,
): string {
  return gamingRoundSeq(windowStartTs(round), slotSec, timeZone);
}

/**
 * The round's date in the ANCHOR zone, e.g. "2026-07-12" — the gaming day its
 * `#NNN` belongs to, which is what a detail screen must print beside that
 * number. Not the viewer's calendar date; list headings use `localDayKey`.
 */
export function roundDateFromRound(
  round: RoundLike,
  timeZone: string | undefined = anchorTimeZone,
): string {
  const r = gamingRound(windowStartTs(round), SLOT_SEC, timeZone);
  return r ? r.date : '';
}

export function roundLabelFromRound(round: RoundLike): string {
  return gamingRoundLabel(windowStartTs(round));
}

/**
 * The anchor round of a raffle, as a RoundLike, from the raffle's `scheduled_at`.
 *
 * The backend binds a raffle to the first round whose `ends_at >= scheduled_at`
 * (`JdbcRoundRepository.findAnchorForRaffle`), i.e. the round that ENDS at the
 * draw time — a raffle scheduled 18:00Z anchors on round 17:55Z–18:00Z. So
 * `scheduled_at` is that round's exclusive right boundary, never its start.
 * Feeding it in as `starts_at` numbered the result card one round ahead of the
 * room, Milhar and the archive for the very same draw (#181 vs #180, D-29).
 *
 * Product rule: the draw at 00:05 is #001, and #001–#288 name the same 288
 * draws on every screen.
 */
export function raffleAnchorRound(scheduledAtSec: number | null | undefined): RoundLike {
  const endsAt = Number(scheduledAtSec || 0);
  return endsAt > 0 ? { ends_at: endsAt } : null;
}

// Same as gamingRoundLabel but with a localized "draw" word in front, so the date
// reads as the round's draw date. Pass t('roundDrawDay') as the prefix.
// e.g. "Sorteio 2026-07-12 · #044". '' when the timestamp is missing.
export function drawDayLabel(tsInWindowSec: number | null | undefined, prefix: string): string {
  const label = gamingRoundLabel(tsInWindowSec);
  return label ? `${prefix} ${label}` : '';
}
